import { useState } from "react"

import { Chip, Stack, Typography } from "@mui/material"
import DatePicker from "components/common/datePicker/DatePicker"
import Dialog from "components/common/dialog/dialog"
import DynamicIcon from "components/common/icon/dynamicIcon"
import IconWrapper from "components/common/icon/iconWrapper"
import { Dayjs } from "dayjs"
import { useTranslation } from "react-i18next"
import { IDashboardPeriodParams } from "ts/interfaces/AdminDashboard"
import dayjs from "utils/dayjs"

import { filterChipSx } from "./FilterBar.styles"
import { validateCustomRange } from "./useDashboardPeriod"

export const formatRange = (from?: string, to?: string) => {
  const start = dayjs(from)
  const end = dayjs(to)
  const startFormat = start.isSame(end, "year") ? "MMM DD" : "MMM DD, YYYY"
  return `${start.format(startFormat)} – ${end.format("MMM DD, YYYY")}`
}

interface CustomRangeDialogProps {
  params: IDashboardPeriodParams
  onApply: (from: Dayjs, to: Dayjs) => void
}

// Remount it (key) when the applied range changes so the draft starts from the URL.
const CustomRangeDialog = ({ params, onApply }: CustomRangeDialogProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState<Dayjs | null>(
    params.date_from ? dayjs(params.date_from) : null,
  )
  const [to, setTo] = useState<Dayjs | null>(
    params.date_to ? dayjs(params.date_to) : null,
  )
  const error = validateCustomRange(from, to)
  const isCustom = params.preset === "custom"

  const close = () => {
    setFrom(params.date_from ? dayjs(params.date_from) : null)
    setTo(params.date_to ? dayjs(params.date_to) : null)
    setOpen(false)
  }

  return (
    <>
      <Chip
        label={
          isCustom
            ? formatRange(params.date_from, params.date_to)
            : t("adminDashboard.pickDates")
        }
        icon={
          <IconWrapper aria-hidden>
            <DynamicIcon name="calendarclock" variant="outlined" />
          </IconWrapper>
        }
        color={isCustom ? "primary" : "default"}
        variant={isCustom ? "filled" : "outlined"}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        sx={{
          ...filterChipSx(isCustom),
          "& .MuiChip-icon": {
            m: 0,
            mr: 1,
            color: isCustom ? "common.white" : "primary.main",
          },
        }}
      />
      <Dialog
        open={open}
        onClose={close}
        titleText={t("adminDashboard.customTimeSpan")}
        actionAcceptText={t("adminDashboard.apply")}
        actionAcceptButtonProps={{ disabled: !!error }}
        onActionButtonClick={() => {
          if (from && to) onApply(from, to)
          setOpen(false)
        }}
      >
        <Stack>
          <Stack direction={{ mobile: "column", tablet: "row" }} gap={2}>
            <DatePicker
              label={t("adminDashboard.dateFrom")}
              value={from}
              onChange={(value) => setFrom(value as Dayjs | null)}
              disableFuture
            />
            <DatePicker
              label={t("adminDashboard.dateTo")}
              value={to}
              onChange={(value) => setTo(value as Dayjs | null)}
              disableFuture
              minDate={from ?? undefined}
            />
          </Stack>
          <Typography variant="body2" color="error" mt={1} role="alert">
            {(from || to) && error
              ? t(`adminDashboard.customRangeError_${error}`)
              : " "}
          </Typography>
        </Stack>
      </Dialog>
    </>
  )
}

export default CustomRangeDialog
